import { ProductImage, ImageSources } from "../../../types/experiences";
import { useEffect, useRef, useState } from "react";
import placeholderImage from "@/assets/imgloading.png";

export const SkeletonLoaderItem = () => {
  return (
    <div className="skeleton-card animate-pulse h-full w-full">
      <img
        src={placeholderImage}
        className="h-48 w-full object-cover rounded-md"
        alt=""
      />
      <div className="flex flex-row items-center justify-between w-full mt-2">
        <div className="flex flex-col items-start justify-center w-full space-y-1">
          <div className="h-7 w-44 bg-gray-300 rounded-lg" />
          <div className="flex flex-row items-center space-x-2">
            <div className="h-6 w-16 bg-gray-300 rounded-lg" />
            <div className="h-6 w-16 bg-gray-300 rounded-lg" />
          </div>
        </div>
        <div className="flex space-x-1 transform translate-y-2">
          <div className="h-8 w-8 bg-gray-300 rounded-lg" />
        </div>
      </div>
    </div>
  );
};

export const typeToIconClass: Record<string, string> = {
  historical: "fa-regular fa-monument",
  religious: "fa-regular fa-church",
  restaurants: "fa-regular fa-fork-knife",
  shopping: "fa-regular fa-shopping-cart",
  clubs: "fa-regular fa-champagne-glasses",
  theatre: "fa-regular fa-theater-masks",
  museums: "fa-regular fa-building-columns",
  museum: "fa-regular fa-building-columns",
  "art gallery": "fa-regular fa-palette",
  "national park": "fa-regular fa-trees",
  viewpoint: "fa-regular fa-binoculars",
  parks: "fa-regular fa-tree",
  "street market": "fa-regular fa-store",
  "food market": "fa-regular fa-pot-food",
  restaurant: "fa-regular fa-fork-knife",
  bar: "fa-regular fa-glass-whiskey",
  stadium: "fa-regular fa-court-sport",
  "natural wonder": "fa-regular fa-sparkle",
  "hiking trail": "fa-regular fa-person-hiking",
  wildlife: "fa-regular fa-paw",
  beach: "fa-regular fa-umbrella-beach",
  waterfall: "fa-regular fa-water",
  "amusement park": "fa-regular fa-roller-coaster",
  lake: "fa-regular fa-water-ladder",
  diving: "fa-regular fa-mask-snorkel",
  residential: "fa-regular fa-home",
};

export const mapTypeNames = (
  experience_type: string,
  types: string[]
): string[] => {
  const mappedTypes: string[] = [];

  if (experience_type === "foodAndDrinks") {
    mappedTypes.push("Restaurant");
    return mappedTypes;
  }

  if (types.includes("museum")) {
    mappedTypes.push("Museum");
  }
  if (types.includes("park")) {
    mappedTypes.push("Park");
  }
  if (types.includes("zoo")) {
    mappedTypes.push("Zoo");
  }
  if (types.includes("aquarium")) {
    mappedTypes.push("Aquarium");
  }
  if (types.includes("stadium")) {
    mappedTypes.push("Stadium");
  }
  if (types.includes("church")) {
    mappedTypes.push("Church");
  }
  if (types.includes("library")) {
    mappedTypes.push("Library");
  }
  if (types.includes("cemetery")) {
    mappedTypes.push("Cemetery");
  }
  if (
    types.includes("department_store") ||
    types.includes("shopping_mall") ||
    types.includes("store")
  ) {
    mappedTypes.push("Shopping");
  }
  if (types.includes("amusement_park")) {
    mappedTypes.push("Amusement Park");
  }
  if (types.includes("tourist_attraction")) {
    mappedTypes.push("Attraction");
  } else {
    if (types.includes("point_of_interest")) {
      mappedTypes.push("Point of Interest");
    }
    if (types.includes("establishment")) {
      mappedTypes.push("Establishment");
    }
  }

  if (mappedTypes.length === 0) {
    mappedTypes.push("Other");
  }

  return mappedTypes.slice(0, 2);
};

export const getTransportIcon = (transport: string) => {
  if (transport.includes("bus")) {
    return "bus-simple";
  } else if (transport.includes("train")) {
    return "train";
  } else if (transport.includes("tram")) {
    return "tram";
  } else if (transport.includes("subway")) {
    return "subway";
  } else if (transport.includes("taxi")) {
    return "taxi";
  } else if (transport.includes("ferry")) {
    return "ship";
  } else if (transport.includes("airplane")) {
    return "airplane";
  } else if (transport.includes("rail")) {
    return "train";
  } else if (transport.includes("car")) {
    return "car";
  } else if (transport.includes("boat")) {
    return "boat";
  } else if (transport.includes("bicycle")) {
    return "bicycle";
  } else if (transport.includes("foot")) {
    return "foot";
  } else if (transport.includes("air")) {
    return "airplane";
  } else if (transport.includes("underground")) {
    return "subway";
  } else if (transport.includes("metro")) {
    return "subway";
  } else if (transport.includes("tube")) {
    return "subway";
  } else if (transport.includes("walk")) {
    return "shoe-prints";
  } else if (transport.includes("cycle")) {
    return "bicycle";
  }
  return "car";
};

export const convertToTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const getCoverImageSources = (
  images: ProductImage[]
): ImageSources | null => {
  const coverImage = images.find((img) => img.isCover);

  if (!coverImage) {
    return null;
  }

  const sortedVariants = coverImage.variants.sort(
    (a, b) => b.width * b.height - a.width * a.height
  );

  const highRes = sortedVariants[0]
    ? sortedVariants[0]!.url
    : sortedVariants[1]!.url;

  const targetSize = 200 * 200;
  const lowRes = sortedVariants.reduce((closest: any, current: any) => {
    const closestDiff = Math.abs(closest.width * closest.height - targetSize);
    const currentDiff = Math.abs(current.width * current.height - targetSize);
    return currentDiff < closestDiff ? current : closest;
  }).url;

  return {
    lowRes,
    highRes,
  };
};

export const roundRatingToTwoDecimal = (rating: number) => {
  return Math.round(rating * 100) / 100;
};

export const formatReviewCount = (count: number) => {
  if (count < 1000) {
    return count;
  } else {
    return (count / 1000).toFixed(1) + "k";
  }
};

export const currencyFormatter = (currency: string, price: number) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(price);
};

export function useLazyLoad() {
  const [isIntersecting, setIntersecting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIntersecting(entry!.isIntersecting);
      },
      {
        rootMargin: "100px", // Start loading 100px before the item is in view
        threshold: 0.1,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isIntersecting] as const;
}
